import { loginRequest } from '../authConfig';
import { trackAuthTelemetry } from './authTelemetry';

const INTERACTIVE_FALLBACK_TS_KEY = 'gwc_auth_interactive_fallback_ts';
const INTERACTIVE_FALLBACK_COOLDOWN_MS = 15000;
let interactiveFallbackInFlight = false;

const AUTH_ERROR_CODES = new Set([
  'interaction_required',
  'login_required',
  'consent_required',
  'token_refresh_required',
  'monitor_window_timeout',
  'token_acquisition_timeout',
  'no_tokens_found'
]);

function normalize(value) {
  return String(value || '').toLowerCase();
}

export function isAuthRefreshFailure(error) {
  const code = normalize(error?.errorCode);
  const name = normalize(error?.name);
  const message = normalize(error?.message || error?.errorMessage);

  return (
    AUTH_ERROR_CODES.has(code) ||
    name.includes('interactionrequiredautherror') ||
    message.includes('monitor_window_timeout') ||
    message.includes('token acquisition in iframe failed') ||
    message.includes('login_required') ||
    message.includes('interaction_required')
  );
}

export async function recoverWithInteractiveLogin({ instance, error, account, reason = 'auth_refresh_failure' }) {
  if (!instance || !isAuthRefreshFailure(error)) {
    return false;
  }

  if (interactiveFallbackInFlight) {
    return true;
  }

  const now = Date.now();
  const lastFallbackTs = Number.parseInt(sessionStorage.getItem(INTERACTIVE_FALLBACK_TS_KEY) || '0', 10);
  if (lastFallbackTs > 0 && now - lastFallbackTs < INTERACTIVE_FALLBACK_COOLDOWN_MS) {
    return true;
  }

  interactiveFallbackInFlight = true;
  sessionStorage.setItem(INTERACTIVE_FALLBACK_TS_KEY, String(now));

  const currentPath = window.location.pathname;
  if (!['/login', '/redirect', '/logout'].includes(currentPath)) {
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    sessionStorage.setItem('returnUrl', returnUrl);
  }

  trackAuthTelemetry('auth_recovery_triggered', {
    reason,
    errorCode: error?.errorCode,
    message: error?.message || error?.errorMessage,
    recoveryMode: 'interactive_redirect'
  });

  try {
    // Force a fresh interactive sign-in. Without prompt:'login', a stale KMSI
    // cookie can satisfy the request without establishing a real Entra session,
    // which then fails the subsequent silent token call with AADSTS160021 and loops.
    await instance.loginRedirect({
      ...loginRequest,
      loginHint: account?.username,
      prompt: 'login'
    });
    return true;
  } catch (redirectError) {
    if (redirectError?.errorCode === 'interaction_in_progress') {
      return true;
    }

    trackAuthTelemetry('auth_recovery_failed', {
      reason,
      errorCode: redirectError?.errorCode,
      message: redirectError?.message || redirectError?.errorMessage,
      recoveryMode: 'interactive_redirect'
    });
    return false;
  } finally {
    setTimeout(() => {
      interactiveFallbackInFlight = false;
    }, 1500);
  }
}
